import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { ApiResponse } from 'types';

/**
 * AI transfer control plane — per-destination transfer profiles.
 * Backend: teeoff-cpass-backend /telephony/transfer-profiles (TransferProfile).
 * On write the backend pushes to the voiceflow runtime (VoiceflowSyncService).
 */
export interface TransferProfile {
  id?: number;
  organization_id?: string;
  business_id?: string;
  scope_type: 'destination' | 'destination_series' | 'default';
  scope_value: string;
  mode?: 'SIPRefer' | 'SIPDial' | 'liveAgent' | null;
  refer_to_template?: string;
  dial_dest_template?: string;
  headers?: Record<string, string>;
  enabled?: boolean;
  name?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export const transferProfilesService = {
  async list(organizationId?: string): Promise<ApiResponse<TransferProfile[]>> {
    try {
      const params = organizationId ? { organizationId } : {};
      const response = await axiosInstance.get<ApiResponse<TransferProfile[]>>(
        API_ENDPOINTS.TRANSFER_PROFILES,
        { params }
      );
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch transfer profiles' };
    }
  },

  /** Resolved provider + transfer capabilities for an org (which modes are executable). */
  async capabilities(organizationId?: string): Promise<ApiResponse<any>> {
    try {
      const params = organizationId ? { organizationId } : {};
      const response = await axiosInstance.get<ApiResponse<any>>(
        `${API_ENDPOINTS.TRANSFER_PROFILES}/capabilities`,
        { params }
      );
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch capabilities' };
    }
  },

  async create(data: TransferProfile, organizationId: string): Promise<ApiResponse<TransferProfile>> {
    try {
      const response = await axiosInstance.post<ApiResponse<TransferProfile>>(
        API_ENDPOINTS.TRANSFER_PROFILES,
        data,
        { params: { organizationId } }
      );
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to create transfer profile' };
    }
  },

  async update(id: number, data: Partial<TransferProfile>): Promise<ApiResponse<TransferProfile>> {
    try {
      const response = await axiosInstance.put<ApiResponse<TransferProfile>>(
        API_ENDPOINTS.TRANSFER_PROFILE_DETAIL(id),
        data
      );
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to update transfer profile' };
    }
  },

  async remove(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await axiosInstance.delete<ApiResponse<void>>(
        API_ENDPOINTS.TRANSFER_PROFILE_DETAIL(id)
      );
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to delete transfer profile' };
    }
  }
};
